import { displayLabel } from '../../utils/displayLabels';
import { useDynamicsRecordLink } from '../../hooks/useDynamicsRecordLink';
import { useDynamicsPage } from '../../hooks/useDynamicsPage';
/**
 * DeploiementPage
 *
 * Tableau des déploiements x_deploiement.
 * Clic sur une ligne → modal centré avec détails complets + tables one2many.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Rocket, AlertCircle, Check, X, ChevronRight, Search, Globe2 } from 'lucide-react';
import backendApi from '../../services/backendApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ClientPage from '../common/ClientPage';
import Pagination from '../common/Pagination';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';
import '../../styles/deployments.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatValue(value, fieldDef, compact = false) {
  if (value === false || value === null || value === undefined) {
    return <span className="text-gray-300">—</span>;
  }
  switch (fieldDef.type) {
    case 'boolean':
      return value
        ? <Check className="w-4 h-4 text-green-500 inline" />
        : <X className="w-4 h-4 text-red-400 inline" />;
    case 'date':
    case 'datetime':
      return formatDate(value);
    case 'many2one':
      return Array.isArray(value) ? value[1] : value;
    case 'selection': {
      const selection = fieldDef.selection || [];
      const match = selection.find(([key]) => key === value);
      const label = match ? match[1] : value;
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
          {label}
        </span>
      );
    }
    case 'text':
      if (compact) {
        const s = String(value);
        return s.length > 60 ? s.slice(0, 60) + '…' : s;
      }
      return <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">{String(value)}</p>;
    default:
      return String(value);
  }
}

// ─── Config des champs visibles par section one2many ─────────────────────────

const ONE2MANY_VISIBLE = {
  // valeurs = nom technique OU label normalisé, dans l'ordre d'affichage
  x_studio_us_dployes:        ['priorite', 'code', 'name', 'story points', 'milestone_id'],
  x_studio_parametrages:      ['description', 'responsable'],
  x_studio_modules:           ['description'],
  x_studio_actions_manuelles: ['description', 'responsable'],
  x_studio_procdure_rollback: ['description', 'responsable'],
};

function filterRelatedCols(fieldName, allCols) {
  const whitelist = ONE2MANY_VISIBLE[fieldName];
  if (!whitelist) return allCols;

  // Matching sur le nom technique OU le label normalisé
  const getIdx = (fn, fd) => {
    const byTech = whitelist.indexOf(fn);
    if (byTech !== -1) return byTech;
    return whitelist.indexOf(normalize(fd.string));
  };

  return allCols
    .filter(([fn, fd]) => getIdx(fn, fd) !== -1)
    .sort(([fa, a], [fb, b]) => getIdx(fa, a) - getIdx(fb, b));
}

// ─── Whitelist / ordre des colonnes du tableau ───────────────────────────────

const VISIBLE_FIELDS_ORDER = [
  'environnement',
  'projet',
  'display name',   // → "Description"
  'date deploiement',
  'debut deploiement',
  'fin deploiement',
  'sprint',
  'reussi',         // → "Statut"
];

const LABEL_OVERRIDES = {
  'display name': 'Description',
  'reussi':       'Statut',
};

const EXCLUDED_STRINGS = [
  'description',
  'last update',
  'last updated',
  'date copie production',
  'copie production',
  'date backup',
  'backup',
  'etape',
];

function normalize(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function fieldOrder(fieldDef) {
  const label = normalize(fieldDef.string);
  const idx = VISIBLE_FIELDS_ORDER.findIndex(v => label.includes(v) || v.includes(label));
  return idx === -1 ? 999 : idx;
}

function fieldLabel(fieldDef) {
  const label = normalize(fieldDef.string);
  const match = Object.entries(LABEL_OVERRIDES).find(([k]) => label.includes(k) || k.includes(label));
  return displayLabel(match ? match[1] : (fieldDef.string || ''));
}

function sortColumns(fields) {
  return Object.entries(fields)
    .filter(([fieldName, fieldDef]) => {
      if (fieldName === 'id') return false;
      if (fieldDef.type === 'one2many') return false;
      const label = normalize(fieldDef.string);
      if (EXCLUDED_STRINGS.some(e => label.includes(e))) return false;
      if (fieldName === 'x_name') return true;
      return fieldOrder(fieldDef) < 999;
    })
    .sort(([, a], [, b]) => fieldOrder(a) - fieldOrder(b));
}

function relatedLabel(value) {
  return Array.isArray(value) ? value[1] : value;
}

function deploymentEnvironment(value) {
  const label = normalize(relatedLabel(value));
  if (label.includes('preprod') || label.includes('staging') || label.includes('test')) return 'preprod';
  if (label.includes('production') || label === 'prod') return 'production';
  return 'other';
}

function parseDeploymentDate(value) {
  if (!value) return null;
  const parsed = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function shortTime(value) {
  const parsed = parseDeploymentDate(value);
  return parsed ? parsed.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
}

function durationLabel(start, end) {
  const startDate = parseDeploymentDate(start);
  const endDate = parseDeploymentDate(end);
  if (!startDate || !endDate || endDate <= startDate) return null;
  const minutes = Math.round((endDate - startDate) / 60000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours ? `${hours} h` : ''}${hours && rest ? ' ' : ''}${rest ? `${rest} min` : ''}`;
}

// ─── Modal de détails ────────────────────────────────────────────────────────

const DeploymentModal = ({ deployment, columns, one2manyFields, relatedData, onClose }) => {
  const modalExit = useAnimatedModalClose(onClose);
  if (!deployment) return null;

  const name = deployment.x_name || `Déploiement #${deployment.id}`;

  return (
    <div className={`client-dialog ${modalExit.modalClassName} fixed inset-0 z-[60] flex items-center justify-center p-4`}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={modalExit.close}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-7xl flex flex-col max-h-[95vh] animate-modal-in">

        {/* Header */}
        <div className="flex-none bg-gradient-to-r from-[#6B4C9A] to-[#59407F] px-6 py-5 rounded-t-2xl">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-purple-200 font-medium uppercase tracking-wide">Déploiement</p>
                <h2 className="text-lg font-bold text-white truncate">{name}</h2>
              </div>
            </div>
            <button
              onClick={modalExit.close}
              className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Champs scalaires — grille 2 colonnes */}
          {columns.some(([fn]) => {
            const v = deployment[fn];
            return v !== false && v !== null && v !== undefined;
          }) && (
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              {columns.map(([fieldName, fieldDef]) => {
                const value = deployment[fieldName];
                if (value === false || value === null || value === undefined) return null;
                const isText = fieldDef.type === 'text';
                return (
                  <div key={fieldName} className={isText ? 'col-span-2' : ''}>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">
                      {fieldLabel(fieldDef)}
                    </p>
                    <div className="text-sm text-gray-800 font-medium">
                      {formatValue(value, fieldDef, false)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Séparateur si on a les deux */}
          {columns.some(([fn]) => { const v = deployment[fn]; return v !== false && v !== null && v !== undefined; }) &&
           one2manyFields.some(([fn]) => { const ids = deployment[fn]; return Array.isArray(ids) && ids.length > 0; }) && (
            <hr className="border-gray-100" />
          )}

          {/* Champs one2many — une section par champ */}
          {one2manyFields.map(([fieldName, fieldDef]) => {
            const ids = deployment[fieldName];
            if (!Array.isArray(ids) || ids.length === 0) return null;

            const data = relatedData[fieldName] || { fields: {}, records: [] };
            const relatedRecords = data.records.filter(r => ids.includes(r.id));
            const allCols = Object.entries(data.fields).filter(([k]) => k !== 'id');
            const relatedCols = filterRelatedCols(fieldName, allCols);

            return (
              <div key={fieldName}>
                {/* Titre de la section */}
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-gray-700">{displayLabel(fieldDef.string || fieldName)}</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                    {relatedRecords.length}
                  </span>
                </div>

                {relatedRecords.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Aucun enregistrement</p>
                ) : relatedCols.length === 0 ? (
                  /* Pas de champs connus → afficher juste les IDs */
                  <div className="flex flex-wrap gap-1.5">
                    {relatedRecords.map(r => (
                      <span key={r.id} className="inline-block px-2.5 py-1 text-xs rounded-md bg-purple-50 text-purple-700 border border-purple-100 font-medium">
                        {r.display_name || `#${r.id}`}
                      </span>
                    ))}
                  </div>
                ) : (
                  /* Table des enregistrements liés */
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            {relatedCols.map(([fn, fd]) => (
                              <th key={fn} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                {displayLabel(fd.string || fn)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {relatedRecords.map(record => (
                            <tr key={record.id} className="hover:bg-purple-50/30 transition-colors">
                              {relatedCols.map(([fn, fd]) => {
                                const isText = fd.type === 'text' || fn === 'description' || (fd.string || '').toLowerCase().includes('descri');
                                return (
                                  <td
                                    key={fn}
                                    className={`px-3 py-2.5 text-gray-700 align-top ${isText ? 'whitespace-pre-wrap break-words max-w-sm' : 'whitespace-nowrap'}`}
                                  >
                                    {formatValue(record[fn], fd, !isText)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in {
          animation: modal-in 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
};

// ─── Page principale ─────────────────────────────────────────────────────────

const DeploiementPage = () => {
  const activeProjectId = Number(localStorage.getItem('selected_project_id')) || null;
  const [deployments, setDeployments] = useState([]);
  const [fields, setFields] = useState({});
  const [relatedData, setRelatedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [selectedDeployment, setSelectedDeployment] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    backendApi.getDeployments(activeProjectId)
      .then(data => {
        if (cancelled) return;
        setDeployments(data.deployments || []);
        setFields(data.fields || {});
        setRelatedData(data.relatedData || {});
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [activeProjectId]);

  // Fermer avec Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSelectedDeployment(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Colonnes scalaires du tableau (sans one2many)
  const columns = useMemo(() => sortColumns(fields), [fields]);

  // Champs one2many pour le modal
  const one2manyFields = useMemo(() =>
    Object.entries(fields).filter(([, def]) => def.type === 'one2many'),
  [fields]);

  const fieldKeys = useMemo(() => {
    const entries = Object.entries(fields);
    const byLabel = predicate => entries.find(([, definition]) => predicate(normalize(definition.string)))?.[0];
    return {
      environment: byLabel(label => label.includes('environnement')),
      date: byLabel(label => label.includes('date deploiement') && !label.includes('debut') && !label.includes('fin')),
      start: byLabel(label => label.includes('debut deploiement')),
      end: byLabel(label => label.includes('fin deploiement')),
      sprint: byLabel(label => label.includes('sprint')),
      status: byLabel(label => label.includes('reussi') || label === 'statut'),
    };
  }, [fields]);

  const environmentCounts = useMemo(() => deployments.reduce((counts, deployment) => {
    const kind = deploymentEnvironment(deployment[fieldKeys.environment]);
    if (kind === 'preprod' || kind === 'production') counts[kind] += 1;
    return counts;
  }, { preprod: 0, production: 0 }), [deployments, fieldKeys.environment]);

  const filteredDeployments = useMemo(() => {
    const query = normalize(searchQuery);
    return deployments.filter(deployment => {
      const matchesSearch = !query || normalize(deployment.x_name || deployment.display_name).includes(query);
      const environment = deploymentEnvironment(deployment[fieldKeys.environment]);
      const matchesEnvironment = environmentFilter === 'all' || environment === environmentFilter;
      return matchesSearch && matchesEnvironment;
    });
  }, [deployments, searchQuery, environmentFilter, fieldKeys.environment]);

  useEffect(() => { setPage(1); }, [searchQuery, environmentFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredDeployments.length / 10));
  const currentPage = Math.min(page, pageCount);
  const visibleRecords = filteredDeployments.slice((currentPage - 1) * 10, currentPage * 10);

  const groupedRecords = useMemo(() => {
    const ordered = [...visibleRecords].sort((a, b) => {
      const aDate = parseDeploymentDate(a[fieldKeys.date] || a[fieldKeys.start]);
      const bDate = parseDeploymentDate(b[fieldKeys.date] || b[fieldKeys.start]);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    });
    return ordered.reduce((groups, deployment) => {
      const date = parseDeploymentDate(deployment[fieldKeys.date] || deployment[fieldKeys.start]);
      const key = date ? `${date.getFullYear()}-${date.getMonth()}` : 'undated';
      let group = groups.find(item => item.key === key);
      if (!group) {
        group = {
          key,
          label: date
            ? date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^./, character => character.toUpperCase())
            : 'Date à préciser',
          records: [],
        };
        groups.push(group);
      }
      group.records.push(deployment);
      return groups;
    }, []);
  }, [visibleRecords, fieldKeys.date, fieldKeys.start]);

  const handleRowClick = useCallback((dep) => {
    setSelectedDeployment(prev => prev?.id === dep.id ? null : dep);
  }, []);

  // ─── Rendu ───────────────────────────────────────────────────────────────

  useDynamicsRecordLink(deployments, setSelectedDeployment);
  useDynamicsPage({path:'/dashboard/deploiements',title:'Déploiements',kind:'deployments',recordIds:(selectedDeployment ? [selectedDeployment] : filteredDeployments).map(t => t.id),filters:{search:searchQuery},ready:!loading});

  const headerActions = <div className="deployment-toolbar" aria-label="Filtres des déploiements">
    <div className="deployment-segments" role="group" aria-label="Environnement">
      {[
        ['all', 'Tous', deployments.length],
        ['preprod', 'Préprod', environmentCounts.preprod],
        ['production', 'Production', environmentCounts.production],
      ].map(([value, label, count]) => <button
        key={value}
        type="button"
        aria-pressed={environmentFilter === value}
        onClick={() => setEnvironmentFilter(value)}
      >{label} · {count}</button>)}
    </div>
    <label className="deployment-search">
      <span className="sr-only">Rechercher un déploiement</span>
      <Search size={15} aria-hidden="true" />
      <input type="search" placeholder="Rechercher" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} />
    </label>
  </div>;

  return (
    <ClientPage title="Déploiements" subtitle={loading ? 'Chargement…' : 'Suivez les mises en ligne de votre projet.'} actions={headerActions}>
      <div key={`${environmentFilter}|${searchQuery}|${currentPage}`} className="client-records deployment-records filter-results-motion">
        {loading && (
          <div className="flex items-center justify-center h-40">
            <LoadingSpinner />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Erreur de chargement</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && columns.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Rocket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Aucun champ disponible</p>
          </div>
        )}

        {!loading && !error && columns.length > 0 && filteredDeployments.length === 0 && environmentFilter !== 'production' && (
          <div className="deployment-empty">
            <Rocket size={22} aria-hidden="true" />
            <div><strong>Aucun déploiement trouvé</strong><p>{searchQuery ? 'Modifiez votre recherche pour afficher d’autres résultats.' : 'Les prochaines mises en ligne apparaîtront ici.'}</p></div>
          </div>
        )}

        {!loading && !error && columns.length > 0 && groupedRecords.map(group => (
          <section key={group.key} className="deployment-month" aria-labelledby={`deployment-month-${group.key}`}>
            <h2 id={`deployment-month-${group.key}`}>{group.label}</h2>
            <div className="deployment-timeline" role="table" aria-label={`Déploiements — ${group.label}`}>
              {group.records.map(deployment => {
                const calendarDate = parseDeploymentDate(deployment[fieldKeys.date] || deployment[fieldKeys.start]);
                const environmentValue = relatedLabel(deployment[fieldKeys.environment]);
                const environment = deploymentEnvironment(environmentValue);
                const startValue = deployment[fieldKeys.start] || deployment[fieldKeys.date];
                const start = shortTime(startValue);
                const end = shortTime(deployment[fieldKeys.end]);
                const duration = durationLabel(startValue, deployment[fieldKeys.end]);
                const sprintValue = relatedLabel(deployment[fieldKeys.sprint]);
                const sprint = sprintValue && sprintValue !== '/' ? sprintValue : null;
                const status = deployment[fieldKeys.status];
                return <div
                  key={deployment.id}
                  role="row"
                  tabIndex={0}
                  aria-label={deployment.x_name || deployment.display_name || `Déploiement ${deployment.id}`}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') handleRowClick(deployment); }}
                  onClick={() => handleRowClick(deployment)}
                  className={`deployment-timeline__row ${selectedDeployment?.id === deployment.id ? 'is-selected' : ''}`}
                >
                  <div className="deployment-date" aria-label={calendarDate ? calendarDate.toLocaleDateString('fr-FR') : 'Date à préciser'}>
                    <span>{calendarDate ? calendarDate.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '') : '—'}</span>
                    <strong>{calendarDate ? calendarDate.getDate() : '—'}</strong>
                  </div>
                  <div className="deployment-summary">
                    <strong>{deployment.x_name || deployment.display_name || `Déploiement #${deployment.id}`}</strong>
                    <div className="deployment-meta">
                      {environmentValue && <span className={`deployment-environment deployment-environment--${environment}`}>{environmentValue}</span>}
                      {(start || end) && <span>{start || '—'} → {end || '—'}</span>}
                      {duration && <span>{duration}</span>}
                      <span>{sprint || 'Aucun sprint'}</span>
                    </div>
                  </div>
                  <span className={`deployment-status ${status === true ? 'is-success' : 'is-neutral'}`}>
                    {status === true ? <><Check size={12} /> Réussi</> : '● Statut non renseigné'}
                  </span>
                  <ChevronRight className="deployment-chevron" size={17} aria-hidden="true" />
                </div>;
              })}
            </div>
          </section>
        ))}

        {!loading && !error && columns.length > 0 && environmentCounts.production === 0 && environmentFilter !== 'preprod' && !searchQuery && (
          <div className="deployment-production-empty">
            <span><Globe2 size={17} aria-hidden="true" /></span>
            <div><strong>Aucune mise en production pour l’instant</strong><p>Les déploiements sur l’environnement de production apparaîtront ici.</p></div>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {pageCount > 1 && <Pagination currentPage={currentPage} totalPages={pageCount} totalItems={filteredDeployments.length} itemsPerPage={10} onPageChange={setPage} />}
      <DeploymentModal
        deployment={selectedDeployment}
        columns={columns}
        one2manyFields={one2manyFields}
        relatedData={relatedData}
        onClose={() => setSelectedDeployment(null)}
      />
    </ClientPage>
  );
};

export default DeploiementPage;
